import React, { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import get from 'lodash.get'
import ComponentTreeMap from '../../../../charts/echarts/ComponentTreeMap'
import { useChartContext } from '../../../../charts/echarts/ChartProvider'
import { usePipelineDataContextWithVariables } from '../../provider/DDPDataProvider'

/**
 * Recursively groups flat data into a hierarchical tree structure
 * @param {Array} data - Flat array of data items
 * @param {Array} indexBy - Array of keys to group by hierarchically
 * @param {string} valueAccessor - Accessor to the value field
 * @param {string} nameAccessor - Accessor to the name field (optional)
 * @returns {Array} Hierarchical tree structure
 */
function groupData (data, indexBy, valueAccessor, nameAccessor) {
  if (!Array.isArray(data) || !Array.isArray(indexBy) || indexBy.length === 0) {
    return []
  }

  const [currentKey, ...restKeys] = indexBy

  const groups = data.reduce((acc, item) => {
    const key = get(item, currentKey)
    if (!acc[key]) acc[key] = []
    acc[key].push(item)
    return acc
  }, {})

  return Object.keys(groups).map(key => {
    const items = groups[key]

    if (restKeys.length === 0) {
      // Leaf node: use the value from the accessor
      const value = get(items[0], valueAccessor)
      const name = nameAccessor ? get(items[0], nameAccessor) : key
      return { name, value: Number(value) }
    }

    // Recursive grouping for children
    const children = groupData(items, restKeys, valueAccessor, nameAccessor)
    const value = children.reduce((acc, item) => acc + Number(item.value || 0), 0)
    const name = nameAccessor ? get(items[0], nameAccessor) : key

    return { name, value, children }
  })
}

function DDPTreeMap ({
  pipeline,
  variables,

  // Data structure configuration
  seriesAccessor = 'data', // Accessor to the data array
  indexBy, // Array of keys to group by hierarchically (e.g., ['category', 'subcategory'])
  valueAccessor, // Accessor to the value field (e.g., 'value', 'amount')
  nameAccessor, // Optional accessor to name field (defaults to using the group key)

  // All other props pass through to ComponentTreeMap
  ...restProps
}) {
  const { setSeriesLoading } = useChartContext()
  const { data, isLoading } = usePipelineDataContextWithVariables(pipeline, variables)

  // Process data into hierarchical tree structure
  const treeData = useMemo(() => {
    if (!data) return []

    const rawData = seriesAccessor ? get(data, seriesAccessor) : data

    if (!Array.isArray(rawData)) return []
    if (!indexBy || !valueAccessor) return []

    return groupData(rawData, indexBy, valueAccessor, nameAccessor)
  }, [data, seriesAccessor, indexBy, valueAccessor, nameAccessor])

  // Set loading state
  useEffect(() => {
    setSeriesLoading(restProps.seriesId, isLoading)
  }, [isLoading, restProps.seriesId, setSeriesLoading])

  if (isLoading) return null

  return (
    <ComponentTreeMap
      data={treeData}
      {...restProps}
    />
  )
}

DDPTreeMap.propTypes = {
  // Pipeline configuration
  pipeline: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  variables: PropTypes.object,

  // Data structure configuration
  seriesAccessor: PropTypes.string,
  indexBy: PropTypes.arrayOf(PropTypes.string).isRequired,
  valueAccessor: PropTypes.string.isRequired,
  nameAccessor: PropTypes.string
}

DDPTreeMap.defaultProps = {
  seriesAccessor: 'data'
}

export default DDPTreeMap
